import * as React from 'react'
import { useMemo } from 'react'
import { ActionsColumn } from '../ActionsColumn'
import Typography from '@mui/material/Typography'
import { GridColDef } from '@mui/x-data-grid-pro'
import { IForm } from '@user/store/agreements'
import { ENTITY_TYPES, splitCamelCase } from '@sales-center/common'
import { getCreatedDate } from '../columnHelpers/createdDate'
import { useFormPacketsEnabled } from '@user/store/tenantFeatures/hooks/useFormPacketsEnabled'
import { LightTooltip } from '@sales-center/components'

type Props = {
  canUpdate: boolean
  entityId: number
  entityType: ENTITY_TYPES
  openAgreementCreateDrawer?: (formData?: IForm) => void
}

export function useColumns(props: Props, rows: IForm[] = []) {
  const formPacketsEnabled = useFormPacketsEnabled()
  const { openAgreementCreateDrawer } = props

  return useMemo<GridColDef<IForm>[]>(() => {
    let dynamicTemplateWidth = 250
    let dynamicFormNameWidth = 200

    if (formPacketsEnabled && rows.length > 0) {
      const longestName = rows.reduce((max, row) => {
        const length = row.templateNames?.join(', ').length || 0
        return Math.max(max, length)
      }, 0)

      dynamicTemplateWidth = Math.min(500, Math.max(150, longestName * 8 + 40))
    }

    const longestFormName = rows.reduce((max, row) => {
      const length = row.formName.length || 0
      return Math.max(max, length)
    }, 0)
    dynamicFormNameWidth = Math.min(
      500,
      Math.max(150, longestFormName * 8 + 40),
    )

    const cols = [
      {
        field: 'col0',
        headerName: '',
        width: 230,
        sortable: false,
        renderCell: ({ row }) => (
          <ActionsColumn
            {...row}
            {...props}
            agreementId={row.id}
            history={row.history}
            openAgreementCreateDrawer={() => openAgreementCreateDrawer?.(row)}
          />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        renderCell: ({ row }) => (
          <Typography>
            <i>{splitCamelCase(row.status)}</i>
          </Typography>
        ),
        sortable: false,
        width: 200,
      },
      {
        field: 'createdDate',
        headerName: 'Date Created',
        sortable: false,
        type: 'date',
        valueGetter: getCreatedDate,
        width: 200,
      },
      {
        field: 'formName',
        headerName: 'Agreement Name',
        sortable: false,
        width: 200,
        renderCell: ({ row }) => (
          <LightTooltip
            key={`${row.id}-formName`}
            title={dynamicFormNameWidth >= 200 ? row.formName : ''}
          >
            <Typography
              noWrap
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {row.formName}
            </Typography>
          </LightTooltip>
        ),
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        sortable: false,
        width: 200,
      },
    ]

    if (formPacketsEnabled) {
      cols.push({
        field: 'templateNames',
        headerName: 'Packet Templates',
        sortable: false,
        width: 250,
        renderCell: ({ row }) => (
          <LightTooltip
            key={`${row.id}-templateNames`}
            title={
              dynamicTemplateWidth >= 250 && row.templateNames?.length > 0
                ? row.templateNames?.join(', ')
                : ''
            }
          >
            <Typography
              noWrap
              sx={{
                maxWidth: 250,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {row.templateNames?.length > 0
                ? row.templateNames?.join(', ')
                : '-'}
            </Typography>
          </LightTooltip>
        ),
      })
    }

    return cols
  }, [formPacketsEnabled, rows, props, openAgreementCreateDrawer])
}
